/**
 * Google Translate Coordinator
 * Coordinates Google Translate operations to prevent conflicts with React
 * while allowing normal translation functionality
 */

class GoogleTranslateCoordinator {
    private static instance: GoogleTranslateCoordinator;
    private isTranslating: boolean = false;
    private translationQueue: (() => void)[] = [];
    
    private constructor() {
        this.initialize();
    }

    static getInstance(): GoogleTranslateCoordinator {
        if (!GoogleTranslateCoordinator.instance) {
            GoogleTranslateCoordinator.instance = new GoogleTranslateCoordinator();
        }
        return GoogleTranslateCoordinator.instance;
    }

    private initialize(): void {
        this.setupDOMProtection();
        this.monitorTranslationState();
    }

    /**
     * Setup DOM protection that doesn't interfere with translations
     */
    private setupDOMProtection(): void {
        // Override console.error to suppress but not prevent errors
        const originalConsoleError = console.error;
        console.error = function(...args: any[]) {
            const errorMessage = args[0]?.toString() || '';
            
            // Only suppress the specific removeChild error
            if (errorMessage.includes("Failed to execute 'removeChild'") ||
                errorMessage.includes("The node to be removed is not a child")) {
                console.warn('Google Translate DOM conflict detected and suppressed');
                // Don't prevent the operation, just suppress the error
                return;
            }
            
            originalConsoleError.apply(console, args);
        };

        // Add global error handler that doesn't prevent default
        window.addEventListener('error', (event) => {
            if (event.error?.message?.includes("Failed to execute 'removeChild'")) {
                console.warn('Suppressing removeChild error from Google Translate');
                event.preventDefault();
                return false;
            }
        }, true);
    }

    /**
     * Monitor translation state without blocking
     */
    private monitorTranslationState(): void {
        // Watch for Google Translate initialization
        const checkInterval = setInterval(() => {
            const googleTranslateWidget = document.querySelector('.goog-te-combo');
            if (googleTranslateWidget) {
                clearInterval(checkInterval);
                this.setupTranslationListeners();
            }
        }, 1000);
    }

    /**
     * Setup listeners for translation events
     */
    private setupTranslationListeners(): void {
        const selectElement = document.querySelector('.goog-te-combo') as HTMLSelectElement;
        if (!selectElement) return;

        // Listen for translation changes
        selectElement.addEventListener('change', () => {
            this.isTranslating = true;
            
            // Allow translation to complete
            setTimeout(() => {
                this.isTranslating = false;
                this.processQueue();
            }, 2000);
        });

        // Monitor for translation completion
        const observer = new MutationObserver((mutations) => {
            // Check if translation is happening
            const hasTranslateClass = document.documentElement.classList.contains('translated-ltr') ||
                                    document.documentElement.classList.contains('translated-rtl');
            
            if (hasTranslateClass && !this.isTranslating) {
                this.isTranslating = true;
                setTimeout(() => {
                    this.isTranslating = false;
                }, 1000);
            }
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });
    }

    /**
     * Process any queued operations
     */
    private processQueue(): void {
        while (this.translationQueue.length > 0) {
            const operation = this.translationQueue.shift();
            if (operation) operation();
        }
    }

    /**
     * Check if it's safe to perform DOM operations
     */
    public isSafeForDOMOperation(): boolean {
        return !this.isTranslating;
    }

    /**
     * Queue an operation if translation is in progress
     */
    public queueOrExecute(operation: () => void): void {
        if (this.isTranslating) {
            this.translationQueue.push(operation);
        } else {
            operation();
        }
    }

    /**
     * Force allow translations (called when user changes language)
     */
    public allowTranslation(): void {
        this.isTranslating = false;
        
        // Clear any flags that might block translation
        sessionStorage.setItem('allowGoogleTranslate', 'true');
        
        // Process any queued operations after a delay
        setTimeout(() => {
            this.processQueue();
        }, 500);
    }
}

// Initialize coordinator
const coordinator = GoogleTranslateCoordinator.getInstance();

// Export for global access
(window as any).googleTranslateCoordinator = coordinator;

export default coordinator;